/**
 * Upload Standard Dialog
 *
 * Modal for uploading a new official standard document.
 * Allows selecting an existing audit template or creating a new one,
 * then uploading a PDF/DOCX file.
 */

// TODO: Add proper admin role check before allowing mutations

import { useState, useCallback, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { Upload, FileText, X, Loader2, Plus } from "lucide-react";
import { Button, Input } from "@/components/ui/primitives";
import { useAuditTemplates } from "@/hooks/use-audit-templates";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useTranslation, getCurrentLanguage } from "@/packages/utils";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";

const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

interface UploadStandardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadStandardDialog({
  open,
  onOpenChange,
}: UploadStandardDialogProps) {
  const { t } = useTranslation(["admin", "common"]);
  const workspaceId = useWorkspaceId();
  const { templates, isLoading: templatesLoading } = useAuditTemplates();
  const generateUploadUrl = useApiMutation(
    api.admin_standards_mutations.generateUploadUrl,
    { name: "generate-upload-url", silentSuccess: true },
  );
  const uploadStandard = useApiMutation(
    api.admin_standards_mutations.uploadStandard,
    {
      name: "upload-standard",
      successMessage: t("admin:standards.upload.successMessage"),
    },
  );
  const createTemplateMutation = useApiMutation(
    api.admin_standards_mutations.createTemplate,
    {
      name: "create-template",
      successMessage: t("admin:standards.upload.templateCreated"),
    },
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<
    Id<"auditTemplates"> | ""
  >("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // New template inline form state
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateCode, setNewTemplateCode] = useState("");
  const [newTemplateName, setNewTemplateName] = useState("");
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedTemplateId("");
    setSelectedFile(null);
    setIsUploading(false);
    setError(null);
    setShowNewTemplate(false);
    setNewTemplateCode("");
    setNewTemplateName("");
    setIsCreatingTemplate(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    resetForm();
    onOpenChange(false);
  }, [isUploading, resetForm, onOpenChange]);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!isAcceptedFile(file)) {
        setError(t("admin:standards.upload.errors.invalidFileType"));
        return;
      }
      setError(null);
      setSelectedFile(file);
    },
    [t],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleCreateTemplate = useCallback(async () => {
    if (!newTemplateCode.trim() || !newTemplateName.trim()) return;

    setIsCreatingTemplate(true);
    setError(null);
    try {
      const templateId = await createTemplateMutation.mutateAsync({
        code: newTemplateCode.trim().toUpperCase(),
        displayName: newTemplateName.trim(),
      });
      setSelectedTemplateId(templateId as Id<"auditTemplates">);
      setShowNewTemplate(false);
      setNewTemplateCode("");
      setNewTemplateName("");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("admin:standards.upload.templateCreateFailed");
      setError(message);
    } finally {
      setIsCreatingTemplate(false);
    }
  }, [newTemplateCode, newTemplateName, createTemplateMutation, t]);

  const handleSubmit = useCallback(async () => {
    if (!selectedTemplateId || !selectedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadUrl = await generateUploadUrl.mutateAsync(
        {} as Record<string, never>,
      );

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type || "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };

      await uploadStandard.mutateAsync({
        templateId: selectedTemplateId as Id<"auditTemplates">,
        documentStorageId: storageId,
        fileName: selectedFile.name,
        locale: getCurrentLanguage(),
        workspaceId,
      });

      resetForm();
      onOpenChange(false);
    } catch {
      setError(t("admin:standards.upload.errors.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  }, [
    selectedTemplateId,
    selectedFile,
    generateUploadUrl,
    uploadStandard,
    resetForm,
    onOpenChange,
    t,
    workspaceId,
  ]);

  const isFormValid = selectedTemplateId !== "" && selectedFile !== null;

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[480px] rounded-[12px] bg-card shadow-xl"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <Dialog.Title className="text-[15px] font-semibold text-primary">
                      {t("admin:standards.upload.title")}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-card-hover transition-colors"
                        aria-label={t("admin:standards.dialogs.closeDialog")}
                      >
                        <X size={16} className="text-muted" />
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-4">
                    {/* Template selection */}
                    <div>
                      <label
                        htmlFor="template-select"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("admin:standards.upload.auditTemplate")}
                      </label>

                      {showNewTemplate ? (
                        <div className="space-y-2 rounded-lg border border-border bg-card-hover/20 p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[12px] font-medium text-secondary">
                              {t("admin:standards.upload.newTemplate")}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setShowNewTemplate(false);
                                setNewTemplateCode("");
                                setNewTemplateName("");
                                setError(null);
                              }}
                              className="text-[11px] text-muted hover:text-primary transition-colors"
                            >
                              {t("common:actions.cancel")}
                            </button>
                          </div>
                          <Input
                            placeholder={t(
                              "admin:standards.upload.codePlaceholder",
                            )}
                            value={newTemplateCode}
                            onChange={(e) =>
                              setNewTemplateCode(e.target.value.toUpperCase())
                            }
                            disabled={isCreatingTemplate}
                          />
                          <Input
                            placeholder={t(
                              "admin:standards.upload.namePlaceholder",
                            )}
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            disabled={isCreatingTemplate}
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleCreateTemplate}
                            disabled={
                              !newTemplateCode.trim() ||
                              !newTemplateName.trim() ||
                              isCreatingTemplate
                            }
                            loading={isCreatingTemplate}
                            className="w-full"
                          >
                            {t("admin:standards.upload.createTemplate")}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <select
                            id="template-select"
                            value={selectedTemplateId}
                            onChange={(e) =>
                              setSelectedTemplateId(
                                e.target.value as Id<"auditTemplates"> | "",
                              )
                            }
                            disabled={templatesLoading || isUploading}
                            className="w-full h-9 px-3 rounded-lg text-[13px] border border-border bg-card text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors disabled:opacity-50"
                          >
                            <option value="">
                              {templatesLoading
                                ? t("admin:standards.upload.loadingTemplates")
                                : t("admin:standards.upload.selectTemplate")}
                            </option>
                            {templates?.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.code} — {t.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowNewTemplate(true)}
                            disabled={isUploading}
                            className="flex items-center gap-1.5 text-[12px] font-medium text-accent hover:text-accent/80 transition-colors disabled:opacity-50"
                          >
                            <Plus size={14} />
                            {t("admin:standards.upload.createNewTemplate")}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* File drop zone */}
                    <div>
                      <label
                        htmlFor="standard-document-input"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("admin:standards.upload.standardDocument")}
                      </label>

                      {selectedFile ? (
                        <div className="flex items-center gap-3 rounded-lg border border-border bg-card-hover/30 px-4 py-3">
                          <div className="rounded-md p-1.5 bg-accent/10 shrink-0">
                            <FileText size={16} className="text-accent" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-primary truncate">
                              {selectedFile.name}
                            </p>
                            <p className="text-[11px] text-muted">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={() => setSelectedFile(null)}
                              className="p-1 rounded-md hover:bg-card-hover transition-colors"
                              aria-label={t(
                                "admin:standards.dialogs.removeFile",
                              )}
                            >
                              <X size={14} className="text-muted" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          disabled={isUploading}
                          className="w-full rounded-lg border-2 border-dashed border-border hover:border-accent/50 bg-card-hover/20 px-4 py-8 text-center transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Upload
                            size={24}
                            className="mx-auto mb-2 text-muted/60"
                          />
                          <span className="block text-[13px] font-medium text-secondary mb-1">
                            {t("admin:standards.upload.dropZoneText")}
                          </span>
                          <span className="block text-[11px] text-muted">
                            {t("admin:standards.upload.dropZoneSubtext")}
                          </span>
                        </button>
                      )}

                      <input
                        ref={fileInputRef}
                        id="standard-document-input"
                        type="file"
                        accept=".pdf,.docx"
                        onChange={handleFileInputChange}
                        className="hidden"
                        tabIndex={-1}
                      />
                    </div>

                    {/* Error message */}
                    {error && (
                      <p className="text-[12px] text-red-500">{error}</p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
                    <Button
                      variant="secondary"
                      onClick={handleClose}
                      disabled={isUploading}
                    >
                      {t("common:actions.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isUploading}
                      loading={isUploading}
                      icon={isUploading ? Loader2 : Upload}
                    >
                      {isUploading
                        ? t("admin:standards.upload.uploading")
                        : t("admin:standards.upload.uploadButton")}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
